'use client';

import { useState } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes, SelectHTMLAttributes, ReactNode } from 'react';

export function Field({
  label,
  optional,
  hint,
  error,
  success,
  htmlFor,
  children,
}: {
  label?: ReactNode;
  optional?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  success?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-field">
      {label != null && (
        <label className="mx-label" htmlFor={htmlFor}>
          {label}
          {optional && <span className="mx-label__optional"> (opsional)</span>}
        </label>
      )}
      {children}
      {error != null ? (
        <div className="mx-hint mx-hint--error">{error}</div>
      ) : success != null ? (
        <div className="mx-hint mx-hint--success">{success}</div>
      ) : hint != null ? (
        <div className="mx-hint">{hint}</div>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  valid?: boolean;
}
export function Input({ invalid, valid, className, ...props }: InputProps) {
  const cls = ['mx-input', invalid && 'mx-input--error', valid && 'mx-input--success', className].filter(Boolean).join(' ');
  return <input className={cls} aria-invalid={invalid || undefined} {...props} />;
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  valid?: boolean;
}
export function Textarea({ invalid, valid, className, ...props }: TextareaProps) {
  const cls = ['mx-textarea', invalid && 'mx-input--error', valid && 'mx-input--success', className].filter(Boolean).join(' ');
  return <textarea className={cls} aria-invalid={invalid || undefined} {...props} />;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}
export function Select({ invalid, className, children, ...props }: SelectProps) {
  const cls = ['mx-select', invalid && 'mx-input--error', className].filter(Boolean).join(' ');
  return (
    <select className={cls} aria-invalid={invalid || undefined} {...props}>
      {children}
    </select>
  );
}

/** Input dengan tombol aksi trailing (reveal sandi). */
export function PasswordInput({ className, ...props }: InputProps) {
  const [show, setShow] = useState(false);
  return (
    <div className="mx-input-group">
      <Input type={show ? 'text' : 'password'} className={className} {...props} />
      <button
        type="button"
        className="mx-input-group__action"
        aria-pressed={show}
        aria-label={show ? 'Sembunyikan sandi' : 'Tampilkan sandi'}
        onClick={() => setShow((v) => !v)}
      >
        {show ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <path d="M1 1l22 22" />
            <path d="M4.22 4.22A18.5 18.5 0 0 0 2 12s3 8 10 8a9.1 9.1 0 0 0 4.78-1.34" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}
